import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {getMyMenus} from '../api/meApi';
import {pickList} from '../api/http';

const MenuContext = createContext(null);

export const MenuProvider = ({ children }) => {
  const [menus, setMenus] = useState([]);
  const [menuTree, setMenuTree] = useState([]);
  const [permissionMap, setPermissionMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);

  // 트리 -> 플랫 목록.
  const flatten = useCallback((nodes = [], parentId = null, acc = []) => {
    nodes.forEach(n => {
      const item = {
        menuId: n.menuId,
        menuNm: n.menuNm,
        path: n.path,
        rteKey: n.rteKey,
        icon: n.icon,
        parentMenuId: parentId,
        disabled: !!n.disabled,
        useYn: n.useYn ?? 'Y'
      };
      acc.push(item);
      if (n.children && n.children.length) flatten(n.children, n.menuId, acc);
    });
    return acc;
  }, []);

  const buildPermissionMap = (flat = []) => {
    const map = {};
    flat.forEach(m => { map[m.menuId] = { disabled: m.disabled, read: !m.disabled }; });
    return map;
  };

  const loadMenus = useCallback(async () => {
    setLoading(true);
    try {
      const treeRes = await getMyMenus();
      const treeData = pickList(treeRes);
      setMenuTree(treeData);
      const flat = flatten(treeData, null, []);
      setMenus(flat);
      setPermissionMap(buildPermissionMap(flat));
      setLoadedOnce(true);
    } catch (e) {
      setMenus([]);
      setMenuTree([]);
      setPermissionMap({});
      setLoadedOnce(true);
      console.warn('메뉴 조회 실패:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [flatten]);

  // 최초 로드
  useEffect(() => { loadMenus(); }, [loadMenus]);

  // 외부 이벤트로 강제 새로고침
  useEffect(() => {
    const handler = () => loadMenus();
    window.addEventListener('refresh-menus', handler);
    return () => window.removeEventListener('refresh-menus', handler);
  }, [loadMenus]);

  const refreshMenus = () => loadMenus();

  return (
    <MenuContext.Provider value={{ menus, menuTree, permissionMap, loading, loadedOnce, refreshMenus }}>
      {children}
    </MenuContext.Provider>
  );
};

export const useMenuContext = () => useContext(MenuContext);
